import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download } from "lucide-react";
import { statusLabel, STATUS_COLORS, money, when, pretty } from "./claimsShared";

export default function AllDecisionsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [f, setF] = useState({ date_from: "", date_to: "", channel: "", customer_id: "", reviewer: "", decision: "", amount_min: "", amount_max: "", late: "" });

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data?.customers || r.data || [])).catch(() => {});
  }, []);

  const params = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); });
    return p;
  }, [f]);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/claims/decisions?${params()}`).then((r) => setRows(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Failed to load decisions"))
      .finally(() => setLoading(false));
  }, [params]);
  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    const p = params(); p.set("export", "csv");
    const res = await api.get(`/claims/decisions?${p}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = "claim_decisions.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  return (
    <div className="space-y-4" data-testid="all-decisions-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-zinc-900">All Claims (Admin)</h1>
        <Button size="sm" variant="outline" onClick={exportCsv} data-testid="export-decisions-btn">
          <Download className="w-4 h-4 mr-1" /> Export CSV
        </Button>
      </div>

      <div className="grid md:grid-cols-4 gap-3 bg-white border border-zinc-200 rounded-lg p-3" data-testid="decisions-filters">
        <div><Label className="text-xs">From</Label><Input type="date" className="h-8 text-xs" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} data-testid="filter-date-from" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" className="h-8 text-xs" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} data-testid="filter-date-to" /></div>
        <div><Label className="text-xs">Channel</Label><Input className="h-8 text-xs" value={f.channel} onChange={(e) => set("channel", e.target.value)} data-testid="filter-channel" /></div>
        <div>
          <Label className="text-xs">Customer Name</Label>
          <select className="w-full h-8 border border-zinc-200 rounded px-1 text-xs" value={f.customer_id}
            onChange={(e) => set("customer_id", e.target.value)} data-testid="filter-customer">
            <option value="">All customers</option>
            {customers.map((c) => (<option key={c._id} value={c._id}>{c.name}</option>))}
          </select>
        </div>
        <div><Label className="text-xs">Reviewer</Label><Input className="h-8 text-xs" value={f.reviewer} onChange={(e) => set("reviewer", e.target.value)} data-testid="filter-reviewer" /></div>
        <div>
          <Label className="text-xs">Decision</Label>
          <select className="w-full h-8 border border-zinc-200 rounded px-1 text-xs" value={f.decision} onChange={(e) => set("decision", e.target.value)} data-testid="filter-decision">
            <option value="">Any</option>
            {["approve", "partly_approve", "reject"].map((d) => (<option key={d} value={d}>{pretty(d)}</option>))}
          </select>
        </div>
        <div><Label className="text-xs">Min ₹</Label><Input type="number" className="h-8 text-xs" value={f.amount_min} onChange={(e) => set("amount_min", e.target.value)} data-testid="filter-amount-min" /></div>
        <div><Label className="text-xs">Max ₹</Label><Input type="number" className="h-8 text-xs" value={f.amount_max} onChange={(e) => set("amount_max", e.target.value)} data-testid="filter-amount-max" /></div>
        <div>
          <Label className="text-xs">Late only</Label>
          <select className="w-full h-8 border border-zinc-200 rounded px-1 text-xs" value={f.late} onChange={(e) => set("late", e.target.value)} data-testid="filter-late">
            <option value="">No</option><option value="true">Yes</option>
          </select>
        </div>
      </div>

      <div className="border border-zinc-200 rounded-lg overflow-x-auto bg-white">
        <table className="w-full text-xs">
          <thead className="bg-zinc-50 text-zinc-600">
            <tr>{["Claim", "Customer", "Channel", "Type", "Status", "Claim Amount", "Approved Amount", "Decision", "Decided by", "Whose Decision", "Late", "Proof Late", "Time Limit Crossed"].map((h) => (
              <th key={h} className="text-left px-3 py-2 font-semibold whitespace-nowrap">{h}</th>))}</tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={13} className="px-3 py-6 text-center text-zinc-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={13} className="px-3 py-6 text-center text-zinc-400" data-testid="decisions-empty">No decisions match these filters.</td></tr>}
            {rows.map((r) => (
              <tr key={r._id} className="border-t border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                onClick={() => navigate(`/claims/${r._id}`)} data-testid={`decision-row-${r.claim_id}`}>
                <td className="px-3 py-2 font-mono font-semibold">{r.claim_id}</td>
                <td className="px-3 py-2">{r.customer_name}</td>
                <td className="px-3 py-2 text-zinc-500">{r.channel || "—"}</td>
                <td className="px-3 py-2">{pretty(r.claim_type)}</td>
                <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_COLORS[r.status] || "bg-zinc-100"}`}>{statusLabel(r.status)}</span>
                  {r.email_not_sent && <span className="ml-1 px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 text-[10px] font-semibold" data-testid={`decision-email-not-sent-${r.claim_id}`}>Email not sent</span>}
                </td>
                <td className="px-3 py-2">{money(r.claimed_value)}</td>
                <td className="px-3 py-2">{r.approved_value ? money(r.approved_value) : "—"}</td>
                <td className="px-3 py-2">{pretty(r.decision) || "—"}</td>
                <td className="px-3 py-2">{r.decided_by || "—"}</td>
                <td className="px-3 py-2">
                  {r.decision_kind ? (
                    <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${r.decision_kind === "Preliminary" ? "bg-violet-100 text-violet-700" : "bg-emerald-100 text-emerald-700"}`}
                      data-testid={`decision-kind-${r.claim_id}`}>{r.decision_kind}</span>
                  ) : "—"}
                </td>
                <td className="px-3 py-2">{r.is_late ? "Yes" : "No"}</td>
                <td className="px-3 py-2">{r.evidence_late ? "Yes" : "No"}</td>
                <td className={`px-3 py-2 ${r.sla_breaches ? "text-red-600 font-semibold" : ""}`}>{r.sla_breaches}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
